import { PropsWithChildren, useEffect, useImperativeHandle, useState, useCallback } from 'react';
import {
    AdvancedMarker,
    useMap
} from '@vis.gl/react-google-maps';
import { isNullOrEmpty, isNumeric } from '../../../../helpers/ObjectHelpers';
import { NavigationArrow, Square, Circle, Triangle, MapPin } from "phosphor-react-sc";
import MapPropertiesService from '../../services/MapPropertiesService';
import { useNavigate } from 'react-router';
import { isMobile } from '../../../../helpers/DeviceHelpers';
import type { Marker } from '@googlemaps/markerclusterer';
import {
    useSetSelectShipPosition,
    useSetShowInfo,
    useSetShowDialog,
    useShowLocationType,
    useShowLocationQuayManual
} from '../../hooks/useMapStore';

interface MapMarkerRef {
    onSelectedShip: (selectedShipMmsi: number) => void,
    onToggleVisibility: (zoom: number) => void,
}

interface MapMarkerProps {
    ref?: React.Ref<MapMarkerRef>,
    marker: any,
    currentZoom?: number,
    selectedMmsi?: number,
    setMarkerRef?: (marker: Marker | null, key: string) => void;
}

export const MapMarker = ({
    ref = null,
    marker,
    currentZoom = 0,
    selectedMmsi = 0,
    setMarkerRef
}: MapMarkerProps) => {

    const setSelectShipPosition = useSetSelectShipPosition();
    const setShowInfo = useSetShowInfo();
    const setShowDialog = useSetShowDialog();
    const showLocationQuayManual = useShowLocationQuayManual();

    const map = useMap();
    const navigate = useNavigate();

    const mapMarkerTypes = MapPropertiesService.markerTypes;
    const mapProperties = MapPropertiesService.properties;

    const defaultIsVisible = currentZoom === 0 ? true : currentZoom >= mapProperties.MarkersVisibleAtZoom;

    const [isVisible, setIsVisible] = useState(defaultIsVisible);
    const [isSelected, setIsSelected] = useState(false);

    const onSetCenter = () => {
        if (!map) return;
        map.setCenter(marker.position);

        const zoom = map.getZoom();
        if (!zoom || zoom >= mapProperties.MarkersVisibleAtZoom) return;

        map.setZoom(mapProperties.MarkersVisibleAtZoom);
    }

    const onSelectShip = (selectedShipMmsi?:number) => {
        const mmsi = isNumeric(marker.mmsi) ? marker.mmsi : 0;
        const selected = mmsi === selectedShipMmsi;
        
        setIsSelected(selected);
        
        if (!selected) return;

        onSetCenter();
    }

    useImperativeHandle(ref, () => ({
        onSelectedShip(selectedShipMmsi) {
            onSelectShip(selectedShipMmsi);
        },
        onToggleVisibility(zoom: number) {
            onToggleVisibility(zoom);
        },
        onUnselect() {
            setIsSelected(false);
        }
    }));

    useEffect(() => {
        if (selectedMmsi !== marker.mmsi) return;
        setIsSelected(true);
        onSetCenter();

        handleOnClick();
    }, []);

    useEffect(() => {
        if (marker.type !== mapMarkerTypes.Ship) return;
        onSelectShip(selectedMmsi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMmsi]);

    useEffect(() => {
        onToggleVisibility(currentZoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentZoom]);
    
    const markerRef = useCallback(
        
        (callbackMarker: google.maps.marker.AdvancedMarkerElement) =>
            {
                if (marker.type !== "ship") return;
                if (marker && isNumeric(marker.mmsi) && Number(marker.mmsi) > 0) {
                    if (setMarkerRef) {
                        setMarkerRef(callbackMarker, marker.mmsi);
                    }
                }
            },
        [setMarkerRef, marker.mmsi]
    );

    return (
        (isVisible && isValidLatLng(marker.position)) &&
            <AdvancedMarker
                ref={markerRef}
                className={getClassName()}
                zIndex={getZIndex()}
                position={marker.position}
                onClick={handleOnClick}>
                {(() => {
                    switch (marker.type) {
                        case mapMarkerTypes.Ship:
                            return (
                                <MarkerShipContent
                                    marker={marker}
                                    isSelected={isSelected}
                                    highlight={marker.highlight}
                                    onMouseEnter={handleOnMouseEnter}
                                    onMouseLeave={handleOnMouseLeave}
                                />);
                        case mapMarkerTypes.Location:
                            return (
                                <MarkerLocationContent
                                    marker={marker}
                                    isSelected={isSelected}
                                    onMouseEnter={handleOnMouseEnter}
                                    onMouseLeave={handleOnMouseLeave}
                                />
                            );
                        default:
                            return (
                                <MarkerPinContent
                                    marker={marker}
                                    onMouseEnter={handleOnMouseEnter}
                                    onMouseLeave={handleOnMouseLeave}/>
                            )
                    }
                })()}
            </AdvancedMarker>
    )

    function isValidLatLng(position: any): boolean {
        if (!position || !Object.hasOwn(position, "lat") || !Object.hasOwn(position, "lng")) return false;

        const lat = position.lat();
        const lng = position.lng();

        if (!isNumeric(lat) || !isNumeric(lng)) return false;

        return true;
    }

    function getZIndex() {
        if (!Object.hasOwn(marker, "topmost")) return 0;
        return marker.topmost ?  1: 0;
    }

    function getClassName() {
        if (Object.hasOwn(marker, "shipTypeIdentifier")) return "google-map-ship";
        return "";
    }

    function handleOnClick() {
        if (marker.type === "pin") return;

        const shipId = isNumeric(marker.shipId) ? marker.shipId : 0;
        if (!isNullOrEmpty(marker.description) && shipId > 0 && !isMobile()) {
            setShowDialog(marker);
        }
        
        if (isMobile()) {
            if(shipId > 0){
                setSelectShipPosition(marker);
            }
            setShowInfo(marker);
        } else {
            if (!showLocationQuayManual) return;
            const hasQuayManuals = Object.hasOwn(marker, "hasQuayManuals") ? marker.hasQuayManuals : false;
            if (hasQuayManuals && marker.type === mapMarkerTypes.Location) {
                navigate(`/quaymanuals/${marker.id}/proximity?title=${encodeURI(marker.description)}`);
            }
        }
    }

    function handleOnMouseEnter() {
        setIsSelected(true);
        setShowInfo(marker);
    }

    function handleOnMouseLeave() {
        setIsSelected(marker.mmsi === selectedMmsi);
        setShowInfo(null);
    }

    function onToggleVisibility(zoom:number) {
        const visible = zoom >= mapProperties.MarkersVisibleAtZoom;
        if (zoom === 0) {
            setIsVisible(true);
            return;
        }

        if (visible && isVisible) return;

        setIsVisible(visible);
    }
}

interface MarkerShipContentProps {
    marker: any,
    isSelected: boolean,
    highlight: boolean,
    onMouseEnter: () => void,
    onMouseLeave: () => void
}
const MarkerShipContent = ({
    marker,
    isSelected = false,
    highlight = false,
    onMouseEnter,
    onMouseLeave
}: MarkerShipContentProps) => {

    const iconColors = MapPropertiesService.iconColors;
    const shipTypes = MapPropertiesService.shipTypes;
    const shipTypeGroups = MapPropertiesService.shipTypeGroups;
    const mapProperties = MapPropertiesService.properties;

    let size = 24;
    if (Object.hasOwn(marker, "length")) {
        if (marker.length < 75) {
            size = 16;
        }
        if (marker.length > 150) {
            size = 32;
        }
    }

    const parentStyle = {
        marginBottom: -size
    }
    
    return (
        <MarkerContentContainer
            style={parentStyle}
            isSelected={isSelected}
            highlight={highlight}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>
                {
                    (marker.speedOverGround <= mapProperties.MinimumSpeed) ?
                        <Square
                            weight="fill"
                            color={getColor()}
                            size={16}
                            className="svg-icon"
                            style={{
                                transform: `rotate(45deg)`,
                                marginTop: -3
                            }}
                        />
                        :
                        <NavigationArrow
                            weight="fill"
                            size={size}
                            color={getColor()}
                            className="svg-icon"
                            style={{
                                transform: `rotate(${marker.courseOverGround + 45}deg)`,
                            }}
                        />
                }
        </MarkerContentContainer>
    )

    function getColor() {
        if (Object.hasOwn(marker, "shipTypeIdentifier")) {
            if (marker.shipTypeIdentifier === shipTypes.PilotVessel) {
                return iconColors.ShipPilotVessel;
            } else {
                if (Object.hasOwn(marker, "shipTypeGroupIdentifier")) {
                    switch (marker.shipTypeGroupIdentifier) {
                        case shipTypeGroups.Cargo:
                            return iconColors.ShipCargo;
                        case shipTypeGroups.Tanker:
                            return iconColors.ShipTanker;
                        case shipTypeGroups.Passenger:
                            return iconColors.ShipPassenger;
                        case shipTypeGroups.Fish:
                            return iconColors.ShipFish;
                        case shipTypeGroups.Tug:
                            return iconColors.ShipTug;
                        case shipTypeGroups.Auxilliary:
                            return iconColors.ShipAuxillary;
                        default:
                            return iconColors.ShipDefault;
                    }
                } else {
                    return iconColors.ShipDefault;
                }
            }
        }
    }

}

interface MarkerLocationContentProps {
    marker: any,
    isSelected?: boolean,
    onMouseEnter: () => void,
    onMouseLeave: () => void,
}
const MarkerLocationContent = ({
    marker,
    isSelected = false,
    onMouseEnter,
    onMouseLeave
}: MarkerLocationContentProps) => {

    const showLocationType = useShowLocationType();

    const color = showLocationType ? marker.color : "var(--map-location-bg)";

    return (
        <MarkerContentContainer
            style={{ marginBottom: -10 }}
            isSelected={isSelected}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>
            {
                (marker.isEligibleForSpecialPilotageFromTimeLockDeadline) ?
                    <Triangle size={16} weight="fill" color={color} style={{ transform: `rotate(180deg)` }} className="svg-icon" />
                    :
                    <Circle size={16} weight="fill" color={color} className="svg-icon" />
            }
        </MarkerContentContainer>
    );
}

interface MarkerPinContentProps {
    marker: any,
    onMouseEnter: () => void,
    onMouseLeave: () => void
}
const MarkerPinContent = ({
    marker,
    onMouseEnter,
    onMouseLeave
}: MarkerPinContentProps) => {

    const color = isNullOrEmpty(marker.color) ? MapPropertiesService.iconColors.Pin : marker.color;
    return (
        <MarkerContentContainer
            style={{ marginBottom: -11 }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>
            <MapPin
                weight="fill"
                color={color}
                className="svg-icon" size={40}
            />
        </MarkerContentContainer>
    );
}

interface MarkerContentContainerProps {
    style: any,
    isSelected?: boolean,
    highlight?: boolean,
    onMouseEnter: () => void,
    onMouseLeave: () => void
}
const MarkerContentContainer = ({
    children,
    style,
    isSelected = false,
    highlight = false,
    onMouseEnter,
    onMouseLeave
}: PropsWithChildren<MarkerContentContainerProps>) => {
    return (
        <div
            className={`gm-marker-c ${isSelected ? 'gm-marker-c-selected' : ''} ${highlight ? 'gm-marker-c-highlight' : ''}`}
            style={style}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>
            <div/>
            <div style={{ padding: 10, zIndex: 10 } }>
                {children}
            </div>
        </div>
    )
}
